import React from "react";
import { Link } from "react-router-dom";
import {
  Users,
  MessageSquare,
  Layers,
  Cpu,
  Briefcase,
  Search,
  ArrowRight,
  CheckCircle2,
  Cloud,
  HeartHandshake,
  BarChart3,
  LineChart,
  Headphones,
  Settings,
  Building2,
  ShieldCheck,
  Zap,
  Target,
  Globe,
  UserCheck,
  ClipboardList,
  Award,
  GraduationCap,
  BadgeCheck,
  Wallet,
  FileSearch,
} from "lucide-react";
import DynamicHero from "@/components/home/DynamicHero";
import StatsBar from "@/components/home/StatsBar";
import Testimonials from "@/components/home/Testimonials";
import ClientLogosMarquee from "@/components/home/ClientLogosMarquee";
import SuccessStories from "@/components/home/SuccessStories";
import GoogleReviews from "@/components/home/GoogleReviews";
import { CONTACT_INFO } from "@/constants/contact";
import { SERVICE_CATALOG } from "@/data/services";

const FEATURES = [
  {
    icon: Briefcase,
    title: "Employer-Focused Approach",
    text: "Recruitment support shaped around role requirements, team needs and business priorities.",
  },
  {
    icon: MessageSquare,
    title: "Candidate-Centric Communication",
    text: "Clear, respectful and timely coordination throughout the recruitment process.",
  },
  {
    icon: Layers,
    title: "Cross-Functional Hiring",
    text: "Talent support across technology, operations, business and support functions.",
  },
];

const IT_CHIPS = [
  "Software Development", "Java", "Python", "Full Stack", "Data Analytics",
  "Data Science", "Cloud", "DevOps", "QA", "Technical Support",
];

const NON_IT_CHIPS = [
  "HR", "Recruitment", "Finance", "Operations", "Sales",
  "Customer Support", "Back Office", "Administration", "Business Development",
];

// Services rendered on the homepage — pulled from the shared catalog so
// each card can link to its dedicated detail page.
const SERVICES = SERVICE_CATALOG.slice(0, 16);

const WHY = [
  { icon: Zap, title: "Fast Hiring", text: "Focused sourcing pipelines that move quickly without compromising quality." },
  { icon: ShieldCheck, title: "Verified Candidates", text: "Every profile goes through skill relevance and preliminary verification." },
  { icon: UserCheck, title: "Experienced Recruiters", text: "Domain-experienced recruiters across IT, Non-IT and executive functions." },
  { icon: Wallet, title: "Affordable Hiring", text: "Transparent commercials without hidden overheads or lock-in games." },
  { icon: Headphones, title: "Dedicated Support", text: "A named point of contact through the entire recruitment lifecycle." },
  { icon: Award, title: "Industry Expertise", text: "Deep understanding of both technology and business function hiring." },
  { icon: Globe, title: "Pan-India Reach", text: "Sourcing across metros, tier-2 cities and remote/hybrid mandates." },
  { icon: Target, title: "Right-Fit Focus", text: "Screened for role, culture, notice period and compensation alignment." },
];

const PROCESS = [
  { step: "01", title: "Inquiry", text: "Share your hiring requirement — role, skills, timeline and budget." },
  { step: "02", title: "Requirement Analysis", text: "We structure the JD, target profile and evaluation criteria." },
  { step: "03", title: "Candidate Sourcing", text: "Focused sourcing across our network and premium channels." },
  { step: "04", title: "Screening & Interview", text: "Preliminary screening and coordinated interview scheduling." },
  { step: "05", title: "Selection", text: "Offer support, negotiation coordination and closure." },
  { step: "06", title: "Joining", text: "Follow-through until the candidate joins and settles in." },
];

const SECTORS = [
  { icon: Cpu, label: "Information Technology" },
  { icon: BarChart3, label: "Data & Analytics" },
  { icon: Cloud, label: "Cloud & Infrastructure" },
  { icon: HeartHandshake, label: "Human Resources" },
  { icon: LineChart, label: "Finance & Accounts" },
  { icon: Briefcase, label: "Sales & Business Development" },
  { icon: Headphones, label: "Customer Support" },
  { icon: Settings, label: "Operations & Administration" },
];

const STATS = [
  { value: 5000, suffix: "+", label: "Candidates Placed" },
  { value: 300, suffix: "+", label: "Hiring Partners" },
  { value: 20, suffix: "+", label: "Industries Served" },
  { value: 24, suffix: "×7", label: "Recruiter Support" },
];

export default function HomePage() {
  return (
    <div className="flex flex-col">
      <DynamicHero />

      {/* Stats immediately below hero */}
      <StatsBar items={STATS} />

      {/* INTRODUCTION */}
      <section className="section-pad bg-white">
        <div className="container-brand grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?auto=format&fit=crop&w=1200&q=80"
                alt="Recruiter having a professional discussion with a candidate"
                className="w-full h-[480px] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -right-6 hidden md:flex flex-col gap-1 bg-white rounded-2xl shadow-xl border border-brand-border px-6 py-5 w-64">
                <span className="eyebrow">Focus Areas</span>
                <span className="font-heading font-bold text-brand-navy text-xl">IT + Non-IT</span>
                <span className="text-sm text-brand-textSoft">Recruitment across evolving business functions.</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <span className="eyebrow">People-First Recruitment</span>
            <h2 className="mt-3 heading-2">Recruitment Built Around People and Business Goals</h2>
            <p className="mt-5 body-lg">
              At Ascend Tech Solutions, we understand that successful hiring is
              more than filling an open position. It is about aligning skills,
              potential, culture and business requirements. From growing teams
              to evolving career journeys, we support meaningful connections
              between employers and professionals.
            </p>
            <div className="mt-8 grid sm:grid-cols-1 gap-4">
              {FEATURES.map((f) => (
                <div key={f.title} className="card-surface p-5 flex gap-4 items-start">
                  <div className="p-2.5 rounded-lg bg-brand-soft text-brand-royal shrink-0">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-brand-navy text-base">{f.title}</h3>
                    <p className="mt-1 text-sm text-brand-textSoft">{f.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IT & NON-IT */}
      <section className="section-pad bg-brand-soft">
        <div className="container-brand">
          <div className="max-w-3xl">
            <span className="eyebrow">Recruitment Across Sectors</span>
            <h2 className="mt-3 heading-2">Talent Support Across IT and Non-IT Functions</h2>
            <p className="mt-4 body-lg">
              Whether you are building a technology team or expanding your
              business operations, our recruitment support covers both worlds.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-6 lg:gap-8">
            <div className="card-surface overflow-hidden">
              <div className="relative h-56 md:h-64">
                <img src="https://images.unsplash.com/photo-1698047681452-08eba22d0c64?auto=format&fit=crop&w=1200&q=80" alt="Software developers and engineers collaborating" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/10 to-transparent" />
                <div className="absolute bottom-4 left-5 flex items-center gap-2 text-white">
                  <div className="p-2 rounded-lg bg-brand-royal"><Cpu className="h-4 w-4" /></div>
                  <span className="font-heading font-bold">IT Recruitment</span>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <p className="text-brand-textSoft">
                  Supporting technology hiring across software development,
                  data, cloud, infrastructure, quality, support and emerging
                  digital functions.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {IT_CHIPS.map((c) => <span key={c} className="chip">{c}</span>)}
                </div>
              </div>
            </div>
            <div className="card-surface overflow-hidden">
              <div className="relative h-56 md:h-64">
                <img src="https://images.unsplash.com/photo-1758518731296-20e24e58846f?auto=format&fit=crop&w=1200&q=80" alt="HR, operations and business professionals in a meeting" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/10 to-transparent" />
                <div className="absolute bottom-4 left-5 flex items-center gap-2 text-white">
                  <div className="p-2 rounded-lg bg-brand-teal"><Briefcase className="h-4 w-4" /></div>
                  <span className="font-heading font-bold">Non-IT Recruitment</span>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <p className="text-brand-textSoft">
                  Helping organizations recruit professionals across business
                  operations, customer functions, finance, administration,
                  sales and human resources.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {NON_IT_CHIPS.map((c) => <span key={c} className="chip">{c}</span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID (16) */}
      <section className="section-pad bg-white">
        <div className="container-brand">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 max-w-5xl">
            <div>
              <span className="eyebrow">Recruitment Services</span>
              <h2 className="mt-3 heading-2">A Complete Recruitment &amp; Staffing Toolkit</h2>
              <p className="mt-4 body-lg max-w-2xl">
                From permanent hiring to executive search, contract staffing
                and volume recruitment — one partner across the full workforce
                lifecycle.
              </p>
            </div>
            <Link to="/services" className="btn-secondary self-start">
              View All Services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <Link
                key={s.title}
                to={`/services/${s.slug}`}
                className="group card-surface p-5 md:p-6 hover:-translate-y-1 hover:border-brand-royal/40 transition-all"
              >
                <div className="p-2.5 rounded-lg bg-brand-soft text-brand-royal w-fit group-hover:bg-brand-royal group-hover:text-white transition-colors">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading font-bold text-brand-navy">{s.title}</h3>
                <p className="mt-1.5 text-sm text-brand-textSoft leading-relaxed line-clamp-3">{s.tagline}</p>
                <div className="mt-4 h-px bg-brand-border group-hover:bg-brand-teal transition-colors" />
                <div className="mt-3 text-xs font-semibold text-brand-royal flex items-center gap-1">
                  Learn More <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative bg-brand-royal overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url(https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&w=1600)",
            backgroundSize: "cover", backgroundPosition: "center", mixBlendMode: "overlay",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-royal via-brand-royal/95 to-brand-navy/90" />
        <div className="container-brand section-pad relative z-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <span className="eyebrow-light">Why Choose Us</span>
            <h2 className="mt-3 font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Why Businesses Choose Ascend Tech Solutions
            </h2>
            <p className="mt-5 text-white/95 text-base sm:text-lg leading-relaxed font-medium">
              A practical, honest and pan-India recruitment partner — built for
              teams that value hiring quality over hiring noise.
            </p>
            <Link to="/contact?type=employer" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-brand-royal font-semibold text-sm hover:bg-brand-soft transition-colors">
              Start a Conversation <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {WHY.map((w) => (
              <div key={w.title} className="p-5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-brand-teal shrink-0">
                    <w.icon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-heading font-bold text-base">{w.title}</h3>
                    <p className="mt-1 text-white/90 text-sm leading-relaxed">{w.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-pad bg-white">
        <div className="container-brand">
          <div className="max-w-2xl">
            <span className="eyebrow">Our Process</span>
            <h2 className="mt-3 heading-2">A Clear, Practical Recruitment Process</h2>
            <p className="mt-4 body-lg">
              A structured six-step workflow that keeps clients informed and
              candidates respected at every stage.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROCESS.map((p, i) => (
              <div key={p.step} className="relative card-surface p-6">
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-xl bg-brand-royal text-white font-heading font-extrabold text-sm flex items-center justify-center">
                    {p.step}
                  </span>
                  <span className="h-px flex-1 bg-brand-border" />
                  {i < PROCESS.length - 1 && (
                    <ArrowRight className="w-4 h-4 text-brand-teal" />
                  )}
                </div>
                <h3 className="mt-5 font-heading font-bold text-brand-navy text-lg">{p.title}</h3>
                <p className="mt-2 text-sm text-brand-textSoft leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="section-pad bg-brand-soft">
        <div className="container-brand">
          <div className="max-w-3xl">
            <span className="eyebrow">Sectors We Support</span>
            <h2 className="mt-3 heading-2">Talent Across Diverse Business Functions</h2>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {SECTORS.map((s) => (
              <div key={s.label} className="card-surface p-5 flex flex-col items-start gap-3 hover:-translate-y-1 transition-transform">
                <div className="p-2.5 rounded-lg bg-brand-royal/10 text-brand-royal">
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="font-heading font-semibold text-brand-navy text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS / INDUSTRIES MARQUEE */}
      <ClientLogosMarquee />

      {/* SUCCESS STORIES — replaces FAQ */}
      <SuccessStories />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* GOOGLE REVIEWS */}
      <GoogleReviews />

      {/* DUAL CTA */}
      <section className="section-pad bg-white">
        <div className="container-brand grid md:grid-cols-2 gap-6">
          <div className="relative overflow-hidden rounded-2xl">
            <img src="https://images.pexels.com/photos/7580644/pexels-photo-7580644.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/90 via-brand-royal/85 to-brand-royal/70" />
            <div className="relative p-8 md:p-10 text-white min-h-[280px] flex flex-col justify-between">
              <div>
                <div className="p-2.5 rounded-lg bg-white/15 w-fit"><Building2 className="h-5 w-5" /></div>
                <h3 className="mt-5 font-heading font-extrabold text-2xl md:text-3xl leading-tight">
                  Looking for the right people to grow your team?
                </h3>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact?type=employer" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-brand-royal font-semibold hover:bg-brand-soft transition-colors">
                  Discuss Your Hiring Need <ArrowRight className="h-4 w-4" />
                </Link>
                <a href={CONTACT_INFO.callHrefTel} className="btn-ghost-light">Call Now</a>
              </div>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl">
            <img src="https://images.pexels.com/photos/7581111/pexels-photo-7581111.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#0EA5A8]/90 via-brand-teal/85 to-brand-navy/70" />
            <div className="relative p-8 md:p-10 text-white min-h-[280px] flex flex-col justify-between">
              <div>
                <div className="p-2.5 rounded-lg bg-white/15 w-fit"><Search className="h-5 w-5" /></div>
                <h3 className="mt-5 font-heading font-extrabold text-2xl md:text-3xl leading-tight">
                  Exploring your next professional opportunity?
                </h3>
              </div>
              <div className="mt-6">
                <Link to="/contact?type=candidate" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-brand-teal font-semibold hover:bg-brand-soft transition-colors">
                  Connect With Us <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
